<template>
  <div class="input-form">

    <div v-if="keyname.includes('URL')" class="explanation">도큐먼트 정보가 공유됩니다. 개인 URL 대신 학술DB나 출판사 URL을 입력해주세요.</div>

    <!-- Text -->
    <div v-if="category=='text'" class="input-field">
      <div class="input-label">
        <span>{{ name }}</span>
        <span v-if="isrequired" class="required">*</span>
      </div>
      <div v-if="dates.includes(keyname)">
        <div class="date">ex) 2019, 1984[1907], 2018(平成31年), 2019-12-31</div>
        <v-text-field
          outlined flat
          v-model.lazy="new_data"
          @change="update()"
          :rules="requiredRules"
        ></v-text-field>
        
      </div>
      <div v-else>
        <v-text-field
          outlined flat
          v-model.lazy="new_data"
          @change="update()"
          :rules="requiredRules"
        ></v-text-field>
      </div> 
    </div>

    <!-- Text-loading -->
    <div v-if="category=='text-loading'" class="input-field">
      <div class="input-label">
        <span>{{ name }}</span>
        <span v-if="isrequired" class="required">*</span>
      </div>
      <div>
        <v-text-field
          outlined flat
          v-model="new_data"
          @change="update()"
          :rules="requiredRules"
          :loading="suggestion_loading"
          clearable>
        </v-text-field>

        <!-- Suggestions -->
        <transition name="fade">
          <div id="suggestions-modal" 
            v-if="suggestion_modal_visible&&!!suggestions.length"
            v-click-outside="closeSuggestionModal">
            <div class="suggestion-list-wrapper">
              <div class="list-title">
                <span>{{ $t('add_documents.suggestions') }}</span>
                <v-icon @click.stop="closeSuggestionModal">mdi-close</v-icon>
              </div>
              <div v-for="(doc, index) in suggestions" :key="index"
                class="list-row" @click="selectSuggestion(index)">
                <span>[{{ doc_type_model[doc.doc_type]['name'] }}]</span>
                <span class="row-title">{{ doc.title }}</span>
                <span> - </span>
                <span>{{ doc.authors_iso690 }} ( {{ doc.date }} )</span>
                <span v-if="doc.existed">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon
                        small color="carolina_blue" v-on="on">mdi-alert-circle</v-icon>
                    </template>
                    <span v-if="mode=='docs'">
                      {{ $t('import_files.already_in_project') }}
                    </span>
                    <span v-else>
                      {{ $t('import_files.already_in_document') }}
                    </span>
                  </v-tooltip>
                </span>
              </div>
            </div>
          </div>
        </transition>

      </div>
    </div>

    <!-- Select -->
    <div v-if="category=='select'" class="input-field">
      <div class="input-label">
        <span>{{ name }}</span>
        <span v-if="isrequired" class="required">*</span>
      </div>
      <v-select dense
        :items="list_model"
        v-model.trim="new_data"
        @input="update()"
        item-text="name"
        item-value="key">
      </v-select>
    </div>

    <!-- Textarea -->
    <div v-if="category=='textarea'" class="input-field">
      <div class="input-label">
        <span>{{ name }}</span>
        <span v-if="isrequired" class="required">*</span>
      </div>
      <v-textarea
        outlined flat
        v-model="new_data"
        @input="update()"
      ></v-textarea>
    </div>

    <!-- Multiple -->
    <div v-if="category=='multiple'" class="input-field">
      <div class="input-label">
        <span>{{ name }}</span>
        <span v-if="isrequired" class="required">*</span>
      </div>
      <div>
        <div v-for="(value, index) in new_data" :key=index>
          <v-text-field
            outlined flat
            v-model="new_data[index]"
            @input="update()">
            <v-icon slot="append" small
              @click="deleteElement(index)">
                mdi-minus-circle
            </v-icon>
            <v-icon slot="append-outer" small
              @click="addElement(index)">
                add_circle
            </v-icon>
          </v-text-field>
        </div>
      </div>
    </div>

    <!-- Authors -->
    <div v-if="category=='authors'" class="input-field input-field-authors">
      <div class="input-label input-label-top">
        <span>{{ name }}</span>
        <span v-if="isrequired" class="required">*</span>
      </div>
      <div>
        <div class="authors-header">
          <v-checkbox class="check"
            v-model="not_recorded">
            <template v-slot:label>
              {{ $t('add_documents.not_recorded') }}
            </template>
          </v-checkbox>
        </div>
        <div v-if="!not_recorded">
          <div class="text">
            <div v-for="(value, index) in new_data" :key="index"
              class="authors-contents">
              <v-text-field
                outlined flat
                v-model="new_data[index]"
                @input="update()"
                :rules="requiredRules"
                placeholder='Lastname, Firstname'
                single-line>
                <v-icon slot="append" small
                  @click="deleteElement(index)">
                    mdi-minus-circle
                </v-icon>
                <v-icon slot="append-outer" small
                  @click="addElement(index)">
                    add_circle
                </v-icon>
              </v-text-field>     
              <div class="authors-footer" v-if="index==new_data.length-1">
                <v-checkbox class="check"
                  v-if="!(types_no_et_al.includes(parseInt(doc_type)) && keyname=='authors')"
                  v-model="et_al">
                  <template v-slot:label>
                    {{ $t('add_documents.et_al') }}
                  </template>
                </v-checkbox>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { doc_types } from '../../../custom/model'
import { returnDocDetail, returnDocInput } from '../../../custom/function'

export default {
  props: {
    data: {
      required: true,
    },

    keyname: {
      type: String,
    },

    category: {
      type: String,
      default: 'text',
    },

    name: {
      type: String,
      required: true,
    },

    isrequired: {
      type: Boolean,
    },

    model: {
      type: Object,
    },

    complete: {
      type: Boolean,
    },

    query: {
      type: Object,
    },

    document: {
      type: Object,
    },
  },

  data() {
    return {
      doc_type_model: doc_types,
      types_no_et_al: [3,4,103,104],
      list_model: [],

      new_data: this.data,
      not_recorded: false,
      et_al: !this.complete,

      requiredRules: [
        v => (!!v || !this.isrequired) || this.$t('alert.required')
      ],

      suggestion_modal_visible: false,
      suggestion_loading: false,

      query_obj: {},

      raw_suggestions: [],
      suggestions: [],
      sug_modal_top: '450px',
      sug_modal_left: '60px',

      dates: ['date', 'statistics_date', 'address_date', 
        'publishing_date', 'conference_dates', 'year' ],
    }
  },

  computed: {
    ...mapGetters({
      doc_type: 'input/manual_doc_type',
      documents_in_proj: 'project/documents',
      referred_docs: 'document/referred_docs',
    }),

    mode() {
      return !this.$route.query.doc ? 'docs' : 'refs'
    },
  },

  watch: {
    data: {
      handler() {
        this.reviseData()
      },
      deep:true,
      immediate: true,
    },

    doc_type: {
      handler() {
        this.reviseData()
      },
    },

    not_recorded(val) {
      this.update()
    },

    et_al: {
      handler(val) {
        this.$emit('update:complete', !val)
      },
    },

    query: {
      handler(val) {
        if (this.category=='text-loading') {
          this.suggestion_modal_visible = false
        }
      },
      deep: true,
    },

    new_data: {
      handler(val) {
        if (this.category == 'text-loading' && !!val && val!="") {
          this.query_obj = {}
          this.query_obj.title = this.reviseTitle(val)
          if (!!this.reviseAuthors(this.query.authors)) {
            this.query_obj.authors = this.reviseAuthors(this.query.authors)
          }
          this.query_obj.years = this.reviseYear(this.query.year)
          // if (!!this.query_obj.title.length) {
          //   this.getSuggestions()
          // }
        } else {
          this.suggestions = []
        }
      },
    },

  },

  mounted() {
    if (!!this.model) {
      let list = Object.values(this.model)
      this.list_model = list.filter(v => v.type == 'main')
      this.list_model = this.list_model.concat(list.filter(v => v.type =='sub'))
    }
  },

  methods: {
    returnDocDetail,
    returnDocInput,

    reviseData() {
      if (this.category == 'authors') {
        if (this.data.length==1 && this.data[0].name_id==1) {
          this.new_data = [""]
          this.not_recorded = true
        } else {
          this.new_data = this.data.map(v => {
            if (v.lastname.length>0) {
              if (v.firstname == null || v.firstname == "") {
                return v.lastname
              } else {
                return [v.lastname, v.firstname].join(',')
              }
            } else { return "" }
          })
        }
        this.et_al = !this.complete
      } else if (this.keyname == 'keywords') {
        if (this.data.includes(';')) {
          this.new_dait(";")
        } else {
          this.new_data = this.data.split(",")
        }
      } else {
        this.new_data = this.data
      }
    },

    update() {
      if (this.category == 'authors') {
        let result_arr = []
        if (this.not_recorded) {
          result_arr = [{ name_id: 1, ordinal_num: 1 }]
        } else {
          for (let i=0; i<this.new_data.length; i++) {
            let firstname = ""
            let lastname = ""
            let person = JSON.parse(JSON.stringify(this.new_data))[i].split(',')
            lastname = person.splice(0, 1)[0]
            if (person.length>0) {
              firstname = person.join(' ').trim()
            }
            result_arr.push({ lastname, firstname, ordinal_num: i+1 })
          }
        }
        this.$emit('update:data', result_arr)
      }
      else if (this.keyname == 'keywords') {
        this.new_data = this.new_data.map(v => v.trim())
        let join_data = this.new_data.join(";")
        this.$emit('update:data', join_data)
      } else {
        this.$emit('update:data', this.new_data)
      }
    },

    deleteElement(i) {
      if (this.new_data.length>1) {
        this.new_data.splice(i, 1)
        this.update()
      }
    },

    addElement(i) {
      this.new_data.splice(i+1, 0, '')
      this.update()
    },

    closeSuggestionModal() {
      if (this.suggestion_modal_visible) {
        this.suggestion_modal_visible = false
      }
    },

    async getSuggestions() {
      let vm = this
      let error = false
      vm.suggestion_loading = true
      try {
        await this.$store.dispatch('input/getSuggestions', this.query_obj)
      } catch(e) {
        this.$store.commit('error/setDialog', true)
        this.$store.commit('error/setErrorCode', e.response.status)
        error = true
      } finally {
        if (!error) {
          setTimeout(() => {
            vm.suggestions = []
            this.raw_suggestions = this.$store.getters['input/suggestions']
            if (!!this.raw_suggestions.length) {
              this.raw_suggestions.forEach(doc => {
                let sug_doc = {}
                try {
                  sug_doc = vm.returnDocDetail(doc, doc.doc_type)
                } finally {
                  let keys = vm.mode=='docs' ? vm.documents_in_proj.map(v => v.key) :
                    vm.referred_docs.map(v => v.key)
                  sug_doc['existed'] = keys.includes(doc.key) ? true : false
                  vm.suggestions.push(sug_doc)
                }
              })
            }
            vm.suggestion_loading = false
            vm.suggestion_modal_visible = true
          }, 500)
        } else {
          vm.suggestion_loading = false
        }
      }
    },

    selectSuggestion(index) {
      this.$emit('update:document', this.raw_suggestions[index])
    },

    reviseTitle(title) {
      var title_array = []
      var special_pattern = /[\"\';:\-]/gi

      title_array = [title.replace(special_pattern, ' ')
        .split(' ')
        .filter(v => v.trim()!="")
        .map(v => v.trim())]
        
      return title_array
    },

    reviseAuthors(authors) {
      if (Object.keys(authors[0]).includes('name_id')) {
        return null
      } else {
        var author_array = []
        for (let i=0; i<authors.length; i++) {
          let element = []
          let lastname = authors[i].lastname.trim()
          let firstname = authors[i].firstname.trim()
          if (!!lastname.length) { element.push(lastname) }
          if (!!firstname.length) { element.push(firstname) }
          if (!!element.length) { author_array.push(element) }
        }
        return author_array
      }
    },

    reviseYear(year) {
      var year_array = []
      var test_year = parseInt(year.trim())
      if (test_year.toString().length == 4) {
        year_array = [(test_year-1).toString(), test_year.toString(), (test_year+1).toString() ]
        if (test_year.toString() != year) {
          year_array.push(year)
        }
      } else { year_array = [year] }
      return year_array
    },

  },

}
</script>

<style scoped>

  .authors-header {
    display: flex;
    justify-content: flex-start;
  }

  .authors-contents {
    display: grid;
    grid-template-columns: auto 70px;
    align-items: center;
  }

  .authors-footer {
    display: flex;
    height: 100%;
    justify-content: flex-end;
    align-items: flex-end;
  }

  .check {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    height: 100%;
  }

  .v-input {
    padding-top: 5px !important;
  }

  #suggestions-modal {
    border-radius: 2px;
    overflow-y: auto;
    box-shadow: 0 5px 5px -3px rgba(0, 0, 0, .2), 0 8px 10px 1px rgba(0, 0, 0, .14), 0 3px 14px 2px rgba(0, 0, 0, .12);
    min-width: 300px;
    max-height: 300px;
    margin-bottom: 20px;
  }

  .suggestion-list-wrapper > .list-title {
    font-size: 16px;
    font-weight: 700;
    padding: 15px 20px;
    color: #142142;
    display: flex;
    justify-content: space-between;
  }

  .suggestion-list-wrapper > .list-row {
    font-size: 12px;
    padding: 10px 20px;
    line-height: 1.2;
    cursor: pointer;
  }

  .row-title {
    font-size: 13px;
    color: #142142;
  }

  .list-row:hover {
    background-color: whitesmoke;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .input-field {
    display: grid;
    grid-template-columns: 120px auto;
  }

  .input-field-authors {
    margin-top: 16px;
  }

  .input-label {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding-right: 22px;
    padding-top: 10px;
    text-align: end;
  }

  .input-label-top {
    padding-top: 4px;
  }

  .required {
    color: #F65058;
    padding-left: 3px;
  }

  .date {
    font-size: 12px;
  }
  .explanation {
    display: flex;
    justify-content: flex-end;
    color: #FF5193;
    font-size: 12px;
  }

</style>



// WEBPACK FOOTER //
// src/components/shared_components/form/InputVuetify.vue